// src/components/admin/diagnostics/SatPlanWorkspacePage.js
//
// Standalone page (its own browser tab / URL) that hosts the SAT Plan workspace.
// Opened with window.open from the diagnostic report ("Generate SAT Plan") and
// from the student profile Plan tab (Edit / Regenerate / New). Keeping it on its
// own URL means the diagnostic report stays open and the plan workspace is a
// real, shareable page rather than an overlay.
//
// Query params:
//   mode=generate (default) | edit
//   generate: studentId, testId, attemptId, name, label, current, rw, math, target, hours
//   edit:     planId
//   regenerate: planId + mode=generate (loads the plan's source)

import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SatPlanWorkspace from "../dsat/reports/SatPlanWorkspace";
import { fetchPlanById } from "../../../utils/sat/satPlanData";
import { getPlanSubjectScores } from "../../../utils/plan/planConfig";

const numOrUndef = (v) => {
  // NOTE: Number("") === 0, so guard empty/blank explicitly — otherwise a
  // skipped ACT Science (passed as "") would be read as a real score of 0.
  if (v == null || String(v).trim() === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

export default function SatPlanWorkspacePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mode = params.get("mode") === "edit" ? "edit" : "generate";
  const planId = params.get("planId") || "";

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(!!planId);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!planId) return;
    let alive = true;
    setLoading(true);
    fetchPlanById(planId)
      .then((p) => {
        if (!alive) return;
        if (!p) setError("Plan not found.");
        setPlan(p);
      })
      .catch((e) => alive && setError(e?.message || "Failed to load plan."))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [planId]);

  const close = () => {
    window.close();
    // If the tab can't be closed by script (not opened by window.open), go back.
    setTimeout(() => {
      if (!window.closed) window.history.length > 1 ? window.history.back() : window.location.replace("/");
    }, 80);
  };

  if (loading) {
    return <div style={wrap}>Loading plan…</div>;
  }
  if (error) {
    return (
      <div style={wrap}>
        <div style={{ color: "#b91c1c", marginBottom: 12 }}>{error}</div>
        <button style={btn} onClick={close}>
          Close
        </button>
      </div>
    );
  }

  if (saved) {
    return (
      <div style={wrap}>
        <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>✅ Plan saved</div>
        <p style={{ color: "#64748b", marginBottom: 20 }}>
          The plan is saved. You can close this tab and return to the student profile.
        </p>
        <button style={btn} onClick={close}>
          Close this tab
        </button>
      </div>
    );
  }

  // Build source for generate / regenerate.
  let source = {};
  if (mode !== "edit") {
    if (plan) {
      // Regenerate from an existing plan — carry its type + subject scores.
      const ss = getPlanSubjectScores(plan);
      const currentBySubject = {};
      const targetBySubject = {};
      ss.forEach((s) => {
        if (s.current != null) currentBySubject[s.subject] = s.current;
        if (s.target != null) targetBySubject[s.subject] = s.target;
      });
      source = {
        type: plan.type || "SAT",
        studentId: plan.diagnosticStudentId,
        studentName: plan.studentName || params.get("name") || "",
        testId: plan.testId,
        attemptId: plan.sourceAttemptId,
        attemptLabel: plan.attemptLabel,
        sourceCompletedAtMs: plan.sourceCompletedAtMs,
        totalScore: plan.totalScore,
        currentBySubject,
        targetBySubject,
        initialHours: plan.totalHours,
      };
    } else {
      // Fresh generation from a diagnostic report (URL params).
      const testType = params.get("testType") === "ACT" ? "ACT" : "SAT";
      const currentBySubject = {};
      if (testType === "ACT") {
        const m = { English: "english", Math: "math", Reading: "reading", Science: "science" };
        Object.entries(m).forEach(([subj, key]) => {
          const v = numOrUndef(params.get(key));
          if (v != null) currentBySubject[subj] = v;
        });
      } else {
        const rw = numOrUndef(params.get("rw"));
        const ma = numOrUndef(params.get("math"));
        if (rw != null) currentBySubject["Reading & Writing"] = rw;
        if (ma != null) currentBySubject["Math"] = ma;
      }
      source = {
        type: testType,
        studentId: params.get("studentId") || "",
        studentName: params.get("name") || "",
        testId: params.get("testId") || "",
        attemptId: params.get("attemptId") || "",
        attemptLabel: params.get("label") || "",
        totalScore: numOrUndef(params.get("current")),
        currentBySubject,
        initialHours: params.get("hours") || null,
      };
    }
  }

  return (
    <SatPlanWorkspace
      mode={mode === "edit" ? "edit" : "generate"}
      plan={mode === "edit" ? plan : null}
      source={source}
      onClose={close}
      onSaved={(savedId, studentDirId) => {
        // After saving/activating, take the admin straight to this specific
        // plan's detail page (this tab becomes the profile Plan view). If the
        // diagnostic account isn't linked to a profile yet, show the saved screen.
        if (studentDirId) {
          const q = savedId ? `?plan=${encodeURIComponent(savedId)}` : "";
          navigate(`/students/directory/${encodeURIComponent(studentDirId)}/plan${q}`);
        } else {
          setSaved(true);
        }
      }}
    />
  );
}

const wrap = {
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  textAlign: "center",
  fontFamily: "inherit",
  color: "#0f172a",
};
const btn = {
  border: "none",
  background: "#0f172a",
  color: "#fff",
  padding: "10px 20px",
  borderRadius: 10,
  cursor: "pointer",
  fontSize: 14,
  fontWeight: 700,
};
